import { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  ClerkProvider,
  SignIn,
  SignUp as ClerkSignUp,
  SignedIn,
  SignedOut,
  useClerk,
} from "@clerk/clerk-react";
import {
  LayoutDashboard,
  Users,
  Building2,
  Target,
  Mail,
  Settings,
  Activity,
  Calendar,
  Network,
  Video,
  Download,
  FileText,
  HelpCircle,
  Loader2,
  Inbox,
  LogOut,
  Shield,
  Search,
  Layers,
  ShieldCheck,
  Receipt,
  Lightbulb,
  ListFilter,
  Globe,
  Wrench,
  Archive,
  Blocks,
  UserCog,
  LayoutGrid,
  KeyRound,
  Scissors,
  DollarSign,
  Megaphone,
} from "lucide-react";
import { colors, fonts as defaultFonts } from "./styles/theme";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import { HelpPanelProvider } from "./contexts/HelpPanelContext";
import { ErrorBoundary } from "./components/ui";
import {
  fetchMyPageVisibility,
  fetchRolePageAccess,
  loadContactTypeMapping,
} from "./services/api";
import FloatingHelpPanel from "./features/help/FloatingHelpPanel";

// Select Clerk publishable key based on hostname (multi-tenant)
const hostname = window.location.hostname;
const isL30 = hostname.includes("l30") || hostname.includes("latitude30");
const CLERK_PUBLISHABLE_KEY = isL30
  ? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_L30
  : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import Pipeline from "./pages/Pipeline";
import Newsletters from "./pages/Newsletters";
import SettingsPage from "./pages/Settings";
import Editor from "./pages/Editor";

import Companies from "./pages/Companies";
import Engagement from "./pages/Engagement";
import Events from "./pages/Events";
import Register from "./pages/Register";
import Unsubscribe from "./pages/Unsubscribe";
import PublicNewsletter from "./pages/PublicNewsletter";
import NetworkMapPage from "./pages/NetworkMap";
import Meetings from "./pages/Meetings";
import VideoLibraryPage from "./pages/VideoLibrary";
import Downloads from "./pages/Downloads";
import Documents from "./pages/Documents";
import DocEditor from "./pages/DocEditor";
import Help from "./pages/Help";
import ReviewQueue from "./pages/ReviewQueue";
import NewsletterAnalytics from "./pages/NewsletterAnalytics";
import AuditLog from "./pages/AuditLog";
import SendGridActivity from "./pages/SendGridActivity";
import EmailHealth from "./pages/EmailHealth";
import ProductCatalog from "./pages/ProductCatalog";
import ClientAccess from "./pages/ClientAccess";
import Invoices from "./pages/Invoices";
import ChairmanAccess from "./pages/ChairmanAccess";
import SignUp from "./pages/SignUp";
import Enhancements from "./pages/Enhancements";
import AudienceLists from "./pages/AudienceLists";
import ContentPages from "./pages/ContentPages";
import ContentBlocks from "./pages/ContentBlocks";
import NewsletterArchive from "./pages/NewsletterArchive";
import ITAdmin from "./pages/ITAdmin";
import Kanban from "./pages/Kanban";
import UserManagement from "./pages/UserManagement";
import Authenticator from "./pages/Authenticator";
import FinancesDashboard from "./pages/FinancesDashboard";
import SalesDashboard from "./pages/SalesDashboard";
import GlobalSearchModal from "./components/GlobalSearchModal";

const NAV_ITEMS = [
  {
    path: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    roles: ["exec", "admin"],
  },
  {
    path: "/finances",
    label: "Finances",
    icon: DollarSign,
    roles: ["exec", "admin"],
  },
  {
    path: "/sales-dashboard",
    label: "Sales Dashboard",
    icon: Megaphone,
    roles: ["exec", "admin"],
  },
  {
    path: "/contacts",
    label: "Contacts",
    icon: Users,
    roles: ["sales", "exec", "admin"],
  },
  { path: "/review", label: "Review", icon: Inbox, roles: ["exec", "admin"] },
  {
    path: "/companies",
    label: "Companies",
    icon: Building2,
    roles: ["exec", "admin"],
  },
  {
    path: "/catalog",
    label: "Products",
    icon: Layers,
    roles: ["exec", "admin"],
  },
  {
    path: "/client-access",
    label: "Client Access",
    icon: ShieldCheck,
    roles: ["sales", "exec", "admin"],
  },
  {
    path: "/invoices",
    label: "Invoices",
    icon: Receipt,
    roles: ["sales", "exec", "admin"],
  },
  {
    path: "/pipeline",
    label: "Pipeline",
    icon: Target,
    roles: ["sales", "exec", "admin"],
  },
  {
    path: "/newsletters",
    label: "Newsletters",
    icon: Mail,
    roles: ["exec", "admin"],
    requires: "sendgrid",
  },
  {
    path: "/newsletter-archive",
    label: "Archive",
    icon: Archive,
    roles: ["sales", "exec", "admin"],
  },
  {
    path: "/audiences",
    label: "Audiences",
    icon: ListFilter,
    roles: ["exec", "admin"],
  },
  {
    path: "/events",
    label: "Webcasts",
    icon: Calendar,
    roles: ["exec", "admin"],
    requires: "zoom",
  },
  {
    path: "/engagement",
    label: "Engagement",
    icon: Activity,
    roles: ["exec", "admin"],
    requires: "ghl",
  },
  {
    path: "/network",
    label: "Network",
    icon: Network,
    roles: ["exec", "admin"],
  },
  { path: "/videos", label: "Videos", icon: Video, roles: ["exec", "admin"] },
  { path: "/video-library", label: "Clip Library", icon: Scissors, roles: ["exec", "admin"] },
  {
    path: "/downloads",
    label: "Downloads",
    icon: Download,
    roles: ["exec", "admin"],
  },
  {
    path: "/documents",
    label: "Documents",
    icon: FileText,
    roles: ["exec", "admin"],
  },
  {
    path: "/content",
    label: "Content",
    icon: Globe,
    roles: ["exec", "admin"],
  },
  {
    path: "/content-blocks",
    label: "Blocks",
    icon: Blocks,
    roles: ["exec", "admin"],
  },
  {
    path: "/enhancements",
    label: "Enhancements",
    icon: Lightbulb,
    roles: ["exec", "admin"],
  },
  {
    path: "/help",
    label: "Help",
    icon: HelpCircle,
    roles: ["sales", "exec", "admin"],
  },
  { path: "/kanban", label: "Kanban", icon: LayoutGrid, roles: ["exec", "admin"] },
  {
    path: "/authenticator",
    label: "Authenticator",
    icon: KeyRound,
    roles: ["sales", "exec", "admin"],
  },
  { path: "/users", label: "Users", icon: UserCog, roles: ["admin"] },
  { path: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
  {
    path: "/it-admin",
    label: "IT Admin",
    icon: Wrench,
    roles: ["admin", "it-admin"],
  },
  {
    path: "/email-health",
    label: "Email Health",
    icon: ShieldCheck,
    roles: ["admin"],
  },
  { path: "/audit-log", label: "Audit Log", icon: Shield, roles: ["admin"] },
];

function RequireRole({ roles, children }) {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (role === "unassigned") return <Navigate to="/pending" replace />;
  if (!roles.includes(role)) {
    if (role === "it-admin") return <Navigate to="/it-admin" replace />;
    return <Navigate to={role === "sales" ? "/pipeline" : "/"} replace />;
  }
  return children;
}

function WrongTenantBlock() {
  const { signOut } = useClerk();
  const { user } = useAuth();
  const tenant = useTenant();
  const email = user?.userDetails || "your account";
  const domain = email.split("@")[1] || "";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        overflowY: "auto",
        background: colors.bg,
        padding: "2rem",
      }}
    >
      <div
        style={{
          textAlign: "center",
          maxWidth: 460,
          background: colors.cardBg || "#0a1628",
          padding: "2.5rem",
          borderRadius: 12,
          border: `1px solid ${colors.border || "#1e2d45"}`,
        }}
      >
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>&#9888;&#65039;</div>
        <h1 style={{ color: "#fff", fontSize: "1.3rem", margin: "0 0 0.75rem" }}>
          Wrong Sign-In Account
        </h1>
        <p style={{ color: "#c7c7cb", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
          You signed in with <strong style={{ color: "#fff" }}>{email}</strong>, but
          this is the <strong style={{ color: "#fff" }}>{tenant.name}</strong> site.
        </p>
        <p style={{ color: "#c7c7cb", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
          The domain <strong style={{ color: "#fff" }}>@{domain}</strong> is not
          associated with this tenant.
        </p>
        <p style={{ color: "#c7c7cb", lineHeight: 1.6, margin: "0 0 1.5rem" }}>
          Please sign out and use the correct Microsoft account for this site.
        </p>
        <button
          onClick={() => signOut()}
          style={{
            padding: "0.6rem 1.8rem",
            background: colors.accent || "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Sign Out & Try Again
        </button>
      </div>
    </div>
  );
}

function PendingAccess() {
  const { signOut } = useClerk();
  const { user } = useAuth();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        overflowY: "auto",
        background: colors.bg,
        padding: "2rem",
      }}
    >
      <div
        style={{
          textAlign: "center",
          maxWidth: 420,
          background: colors.cardBg || "#0a1628",
          padding: "2.5rem",
          borderRadius: 12,
          border: `1px solid ${colors.border || "#1e2d45"}`,
        }}
      >
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>&#128274;</div>
        <h1 style={{ color: "#fff", fontSize: "1.3rem", margin: "0 0 0.75rem" }}>
          Account Pending Approval
        </h1>
        <p style={{ color: "#c7c7cb", lineHeight: 1.6, margin: "0 0 0.5rem" }}>
          Your account{user?.userDetails ? ` (${user.userDetails})` : ""} has been
          created, but an administrator hasn't assigned your permissions yet.
        </p>
        <p style={{ color: "#c7c7cb", lineHeight: 1.6, margin: "0 0 1.5rem" }}>
          Please contact your administrator to get access.
        </p>
        <button
          onClick={() => signOut()}
          style={{
            padding: "0.5rem 1.5rem",
            background: "transparent",
            color: "#c7c7cb",
            border: `1px solid ${colors.border || "#1e2d45"}`,
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

function HomeRedirect() {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (role === "unassigned") return <Navigate to="/pending" replace />;
  if (role === "it-admin") return <Navigate to="/it-admin" replace />;
  if (role === "sales") return <Navigate to="/pipeline" replace />;
  return <Dashboard />;
}

function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, role, realRole, setRoleOverride, loading } = useAuth();
  const tenant = useTenant();
  const sb = tenant.theme?.sidebar || {};
  const fonts = tenant.theme?.fonts || defaultFonts;
  const [hiddenPages, setHiddenPages] = useState([]);
  const [rolePageConfig, setRolePageConfig] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const roleMenuRef = useRef(null);

  // Ctrl+K / Cmd+K to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Close role menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (roleMenuRef.current && !roleMenuRef.current.contains(e.target)) {
        setRoleMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    // Admins always see all pages — skip page-visibility check
    if (user && role !== "admin") {
      fetchMyPageVisibility()
        .then((data) => setHiddenPages(data.hiddenPages || []))
        .catch(() => {});
    } else {
      setHiddenPages([]);
    }
    // Load contact type mapping for deriveContactType()
    if (user) loadContactTypeMapping();
  }, [user, role]);

  // Load role-level page access config
  useEffect(() => {
    if (user) {
      fetchRolePageAccess()
        .then((data) => setRolePageConfig(data.pages || null))
        .catch(() => {});
    }
  }, [user]);

  const visibleNav = NAV_ITEMS.filter((item) => {
    const roles = rolePageConfig?.[item.path]?.roles || item.roles;
    // Admin always sees everything regardless of config
    if (realRole === "admin" && role === "admin")
      return (
        (!item.requires || tenant.features[item.requires]) &&
        !hiddenPages.includes(item.path)
      );
    return (
      roles.includes(role) &&
      (!item.requires || tenant.features[item.requires]) &&
      !hiddenPages.includes(item.path)
    );
  });

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: colors.bg,
        }}
      >
        <Loader2
          size={24}
          color={colors.accent}
          style={{ animation: "spin 1s linear infinite" }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: colors.bg,
          fontFamily: fonts.body,
          gap: 16,
        }}
      >
        <img src={tenant.logo} alt={tenant.name} style={{ height: 80, marginBottom: 8, objectFit: "contain" }} />
        {window.location.hash.includes("/sign-up") || new URLSearchParams(window.location.search).has("__clerk_ticket")
          ? <ClerkSignUp routing="hash" signInUrl="/#/sign-in" />
          : <SignIn routing="hash" signUpUrl="/#/sign-up" />
        }
      </div>
    );
  }

  // Domain validation: reject users whose email doesn't match this tenant
  const userEmail = user?.userDetails || "";
  const userDomain = userEmail.split("@")[1]?.toLowerCase();
  if (userDomain && tenant.allowedDomains && !tenant.allowedDomains.includes(userDomain)) {
    return <WrongTenantBlock />;
  }

  const { signOut } = useClerk();
  const userInitial = user?.userDetails?.[0]?.toUpperCase() || "?";
  const userName = user?.userDetails || "User";
  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
  const isOverridden = realRole === "admin" && role !== realRole;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: colors.bg,
        fontFamily: fonts.body,
        color: colors.text,
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarCollapsed ? 64 : 240,
          flexShrink: 0,
          background: sb.bg,
          borderRight: `1px solid ${sb.border}`,
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s ease",
          overflowX: "hidden",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: sidebarCollapsed ? "20px 16px" : "20px 24px",
            borderBottom: `1px solid ${sb.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: "pointer",
            minHeight: 65,
          }}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <img
            src={tenant.logo}
            alt={tenant.name}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              flexShrink: 0,
            }}
          />
          {!sidebarCollapsed && (
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: fonts.heading,
                  color: sb.text,
                  whiteSpace: "nowrap",
                }}
              >
                {tenant.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: sb.textSecondary,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {tenant.subtitle}
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: "10px 8px 0" }}>
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "9px 16px",
              borderRadius: 6,
              border: `1px solid ${sb.border}`,
              background: "rgba(255,255,255,0.04)",
              color: sb.textSecondary,
              fontSize: 13,
              fontFamily: fonts.body,
              cursor: "pointer",
              transition: "all 0.15s",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
            }}
          >
            <Search size={16} />
            {!sidebarCollapsed && (
              <>
                <span style={{ flex: 1, textAlign: "left" }}>Search...</span>
                <kbd
                  style={{
                    fontSize: 10,
                    padding: "2px 5px",
                    borderRadius: 3,
                    border: `1px solid ${sb.border}`,
                    color: sb.textSecondary,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {navigator.platform?.includes("Mac") ? "\u2318K" : "Ctrl+K"}
                </kbd>
              </>
            )}
          </button>
        </div>

        {/* Nav */}
        <nav
          className="sidebar-nav"
          style={{ padding: "12px 8px", flex: 1, overflowY: "auto" }}
        >
          {visibleNav.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: isActive ? sb.accentDim : "transparent",
                  color: isActive ? sb.accent : sb.textSecondary,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  marginBottom: 2,
                  justifyContent: sidebarCollapsed ? "center" : "flex-start",
                }}
              >
                <item.icon size={18} />
                {!sidebarCollapsed && (
                  <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div
          ref={roleMenuRef}
          style={{
            padding: sidebarCollapsed ? "16px 0" : "16px 20px",
            borderTop: `1px solid ${sb.border}`,
            display: "flex",
            alignItems: "center",
            gap: 10,
            justifyContent: sidebarCollapsed ? "center" : "flex-start",
            position: "relative",
          }}
        >
          {!sidebarCollapsed && (
            <>
              <div
                onClick={() =>
                  realRole === "admin" && setRoleMenuOpen((v) => !v)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flex: 1,
                  minWidth: 0,
                  cursor: realRole === "admin" ? "pointer" : "default",
                  borderRadius: 6,
                  padding: "4px 6px",
                  margin: "-4px -6px",
                  transition: "background 0.15s",
                  background: roleMenuOpen
                    ? "rgba(255,255,255,0.08)"
                    : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (realRole === "admin")
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  if (!roleMenuOpen)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: isOverridden
                      ? "rgba(245,158,11,0.20)"
                      : "rgba(99,102,241,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isOverridden ? "#f59e0b" : "#6366f1",
                    fontFamily: fonts.heading,
                    fontWeight: 700,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {userInitial}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: sb.text }}
                  >
                    {userName}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: isOverridden ? "#f59e0b" : sb.textSecondary,
                    }}
                  >
                    {isOverridden
                      ? `${roleLabel} (viewing as)`
                      : `${roleLabel} / ${tenant.name}`}
                  </div>
                </div>
              </div>

              {/* Role switcher dropdown (admin only) */}
              {roleMenuOpen && realRole === "admin" && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 12,
                    right: 12,
                    marginBottom: 6,
                    background: sb.bg,
                    border: `1px solid ${sb.border}`,
                    borderRadius: 8,
                    boxShadow: "0 -8px 24px rgba(0,0,0,0.25)",
                    zIndex: 200,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px 6px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: sb.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    View as
                  </div>
                  {[
                    { id: "admin", label: "Admin" },
                    { id: "exec", label: "Executive" },
                    { id: "sales", label: "Sales" },
                  ].map((opt) => {
                    const isActive = role === opt.id;
                    return (
                      <div
                        key={opt.id}
                        onClick={() => {
                          setRoleOverride(opt.id === realRole ? null : opt.id);
                          setRoleMenuOpen(false);
                        }}
                        style={{
                          padding: "9px 14px",
                          fontSize: 13,
                          fontFamily: fonts.body,
                          color: isActive ? sb.accent : sb.text,
                          fontWeight: isActive ? 700 : 400,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            sb.bgHover || "rgba(255,255,255,0.06)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <div
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: isActive ? sb.accent : "transparent",
                            border: isActive
                              ? "none"
                              : `1.5px solid ${sb.textSecondary}`,
                            flexShrink: 0,
                          }}
                        />
                        {opt.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <button
            onClick={() => signOut()}
            title="Log out"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${sb.border}`,
              padding: sidebarCollapsed ? 10 : 8,
              borderRadius: 8,
              cursor: "pointer",
              color: sb.textSecondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = sb.accent;
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = sb.textSecondary;
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            }}
          >
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, overflow: "auto", padding: "28px 36px" }}>
        {children}
      </main>

      <GlobalSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />

      <FloatingHelpPanel />
    </div>
  );
}

function DynamicTitle() {
  const tenant = useTenant();
  useEffect(() => {
    document.title = tenant.pageTitle;
    const link = document.querySelector("link[rel='icon']");
    if (link) link.href = tenant.favicon;
  }, [tenant]);
  return null;
}

export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <TenantProvider>
        <HelpPanelProvider>
          <DynamicTitle />
          <AuthProvider>
            <Routes>
              {/* Public pages — no auth required */}
              <Route path="/register/:eventId" element={<Register />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/public/newsletter/:token" element={<PublicNewsletter />} />
              <Route path="/editor" element={<Editor />} />
              <Route path="/chairmans-access" element={<ChairmanAccess />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/pending" element={<PendingAccess />} />

              {/* All other routes use the shell layout */}
              <Route
                path="*"
                element={
                  <AppShell>
                    <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route
                      path="/finances"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <FinancesDashboard />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/sales-dashboard"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <SalesDashboard />
                        </RequireRole>
                      }
                    />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route
                      path="/review"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <ReviewQueue />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/companies"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Companies />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/catalog"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <ProductCatalog />
                        </RequireRole>
                      }
                    />
                    <Route path="/client-access" element={<ClientAccess />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/pipeline" element={<Pipeline />} />
                    <Route
                      path="/newsletters"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Newsletters />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/audiences"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <AudienceLists />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/newsletters/:id/analytics"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <NewsletterAnalytics />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/email-activity"
                      element={
                        <RequireRole roles={["admin"]}>
                          <SendGridActivity />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/email-health"
                      element={
                        <RequireRole roles={["admin"]}>
                          <EmailHealth />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/events"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Events />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/engagement"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <ErrorBoundary>
                            <Engagement />
                          </ErrorBoundary>
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/network"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <NetworkMapPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/videos"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Meetings />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/video-library"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <VideoLibraryPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/downloads"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Downloads />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/documents"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Documents />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/content"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <ContentPages />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/content-blocks"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <ContentBlocks />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/newsletter-archive"
                      element={
                        <RequireRole roles={["sales", "exec", "admin"]}>
                          <NewsletterArchive />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/doc-editor"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <DocEditor />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/enhancements"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Enhancements />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/help"
                      element={
                        <RequireRole roles={["sales", "exec", "admin"]}>
                          <Help />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/authenticator"
                      element={
                        <RequireRole roles={["sales", "exec", "admin"]}>
                          <Authenticator />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <RequireRole roles={["admin"]}>
                          <UserManagement />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/kanban"
                      element={
                        <RequireRole roles={["exec", "admin"]}>
                          <Kanban />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RequireRole roles={["admin"]}>
                          <SettingsPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/it-admin"
                      element={
                        <RequireRole roles={["admin"]}>
                          <ITAdmin />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="/audit-log"
                      element={
                        <RequireRole roles={["admin"]}>
                          <AuditLog />
                        </RequireRole>
                      }
                    />
                  </Routes>
                </AppShell>
              }
            />
            </Routes>
          </AuthProvider>
        </HelpPanelProvider>
      </TenantProvider>
    </ClerkProvider>
  );
}
